import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  invoiceActions,
  orderActions,
  orderCardActions,
  orderCardServiceLinkageActions,
  OrderFormModalComponent,
  OrderFormModalData,
  orderServiceLinkageActions,
  orderSourceActions,
  orderStatusActions,
  selectOrderCardState,
  selectOrderSourceState,
  selectOrderState,
  selectOrderStatusState,
} from '@aa/angular/core';
import {
  OrderCardResourceTypeMappings,
  OrderResourceTypeMappings,
  OrderSourceResourceTypeMappings,
  OrderStatusResourceTypeMappings,
} from '@aa/nest/resource';
import { INVOICE_STATUS, REFUND_ITEM_STATUS } from '@aa/nest/resource/objects';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  firstValueFrom,
  lastValueFrom,
  map,
  Observable,
  of,
} from 'rxjs';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  computeTextColorForBackground,
  formatAddress,
  formatDateTime,
  isCardLevelChangeFlow,
} from '@aa/ts/common';
import {
  DataTableComponent,
  DataTableConfig,
} from '../../../../../core/src/lib/components/data-table/data-table.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { StaffAppState } from '../../state/index.reducers';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrderCardsTabComponent } from '../../components/order-cards-tab/order-cards-tab.component';
import { OrderInvoicesTabComponent } from '../../components/order-invoices-tab/order-invoices-tab.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MultipleRequestedServicesFormFieldValue } from '../../components/requested-services-form-field/requested-services-form-field.component';
import { OrderActionLogsTabComponent } from '../../components/order-action-logs-tab/order-action-logs-tab.component';
import { OrderService } from 'src/libs/angular/core/src/lib/services/order.service';

@Component({
  selector: 'aa-order-detail-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyMaterialModule,
    FormlyModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    MatMenuModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatBadgeModule,
    DataTableComponent,
    OrderCardsTabComponent,
    OrderInvoicesTabComponent,
    OrderActionLogsTabComponent,
  ],
  templateUrl: './order-detail-view.component.html',
  styleUrl: './order-detail-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderDetailViewComponent {
  summaryTableConfig: DataTableConfig = {
    data$: of([]),
    columns: [
      {
        key: 'attribute',
        label: 'Attribute',
        accessor: (row) => `${row.attribute}: `,
        getStyle: () => ({
          'font-weight': '700',
          'min-width': 'fit-content',
          'text-wrap': 'nowrap',
        }),
        getHeaderStyle: () => ({ minWidth: 'fit-content' }),
      },
      {
        key: 'value',
        label: 'Value',
        getStyle: () => ({ width: '100%' }),
        getHeaderStyle: () => ({ width: '100%' }),
        getLinkValue: (row) => row.href ?? undefined,
      },
    ],
    numItems$: of(0),
    hidePagination: true,
    hideTableHeaders: true,
    hideShadow: true,
  };

  orderId$: Observable<number>;
  order$: Observable<
    OrderResourceTypeMappings['resourceWithRelationsT'] | undefined | null
  >;
  orderStatuses$: Observable<
    OrderStatusResourceTypeMappings['resourceWithRelationsT'][]
  >;
  orderSources$: Observable<
    OrderSourceResourceTypeMappings['resourceWithRelationsT'][]
  >;

  prevOrderId$ = new BehaviorSubject<number | null>(null);
  nextOrderId$ = new BehaviorSubject<number | null>(null);

  currentTabIndex$ = new BehaviorSubject(0);

  isNewFlow$: Observable<boolean>;
  cards$: Observable<OrderCardResourceTypeMappings['resourceWithRelationsT'][]>;

  cardsWithUnsentChanges$: Observable<{
    card: OrderCardResourceTypeMappings['resourceWithRelationsT'];
    addedServices: { name: string; price: number }[];
    removedServices: { name: string; price: number }[];
    netChange: number;
  }[]>;

  cardsWithPendingApproval$: Observable<{
    card: OrderCardResourceTypeMappings['resourceWithRelationsT'];
    addedServices: { name: string; price: number }[];
    removedServices: { name: string; price: number }[];
  }[]>;

  constructor(
    private readonly store: Store<StaffAppState>,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly orderService: OrderService,
  ) {
    this.order$ = this.store
      .select((s) => selectOrderState(s).current)
      .pipe(takeUntilDestroyed());
    this.orderStatuses$ = this.store
      .select((s) => selectOrderStatusState(s).items)
      .pipe(takeUntilDestroyed());
    this.orderSources$ = this.store
      .select((s) => selectOrderSourceState(s).items)
      .pipe(takeUntilDestroyed());

    this.orderId$ = this.route.paramMap.pipe(
      map((paramMap) => parseInt(paramMap.get('id')!)),
      takeUntilDestroyed(),
    );

    this.isNewFlow$ = this.order$.pipe(
      map((order) => isCardLevelChangeFlow(order?.createdAt)),
    );

    this.cards$ = this.store
      .select((s) => selectOrderCardState(s).items)
      .pipe(takeUntilDestroyed());

    this.cardsWithUnsentChanges$ = this.cards$.pipe(
      map((cards) =>
        cards
          .filter((card) => {
            const hasDraftLinkages = card.orderCardServiceLinkages?.some(
              (li: any) => li.isDraft,
            );
            if (!hasDraftLinkages) return false;

            const latestMessage = (card as any).threads?.[0]?.messages?.[0];
            if (latestMessage?.metadata?.status === 'pending') return false;

            return true;
          })
          .map((card) => {
            const draftIds = new Set(
              card.orderCardServiceLinkages
                ?.filter((li: any) => li.isDraft)
                ?.map((li: any) => li.offeredServiceId) ?? [],
            );
            const approvedIds = new Set(
              card.orderCardServiceLinkages
                ?.filter((li: any) => !li.isDraft)
                ?.map((li: any) => li.offeredServiceId) ?? [],
            );

            const addedServices = card.orderCardServiceLinkages
              ?.filter(
                (li: any) => li.isDraft && !approvedIds.has(li.offeredServiceId),
              )
              ?.map((li: any) => ({
                name: li.offeredService?.name ?? '',
                price: parseFloat(
                  li.customPrice ??
                  li.offeredService?.price ??
                  '0',
                ),
              })) ?? [];

            const removedServices = card.orderCardServiceLinkages
              ?.filter(
                (li: any) => !li.isDraft && !draftIds.has(li.offeredServiceId),
              )
              ?.map((li: any) => ({
                name: li.offeredService?.name ?? '',
                price: parseFloat(
                  li.customPrice ??
                  li.offeredService?.price ??
                  '0',
                ),
              })) ?? [];

            const addedTotal = addedServices.reduce((sum, s) => sum + s.price, 0);
            const removedTotal = removedServices.reduce(
              (sum, s) => sum + s.price,
              0,
            );

            return {
              card,
              addedServices,
              removedServices,
              netChange: addedTotal - removedTotal,
            };
          })
          .filter((entry) => entry.addedServices.length > 0 || entry.removedServices.length > 0),
      ),
    );

    this.cardsWithPendingApproval$ = this.cards$.pipe(
      map((cards) =>
        cards
          .filter((card) => {
            const latestMessage = (card as any).threads?.[0]?.messages?.[0];
            return latestMessage?.metadata?.status === 'pending';
          })
          .map((card) => {
            const latestMessage = (card as any).threads?.[0]?.messages?.[0];
            return {
              card,
              addedServices:
                latestMessage?.metadata?.addedServices?.map((s: any) => ({
                  name: s.name,
                  price: parseFloat(s.price ?? '0'),
                })) ?? [],
              removedServices:
                latestMessage?.metadata?.removedServices?.map((s: any) => ({
                  name: s.name,
                  price: parseFloat(s.price ?? '0'),
                })) ?? [],
            };
          }),
      ),
    );

    this.store.dispatch(orderStatusActions.loadItems({}));
    this.store.dispatch(orderSourceActions.loadItems({}));

    this.orderId$.subscribe((orderId) => {
      if (orderId) {
        this.orderService
          .getPreviousOrderId(orderId)
          .subscribe((prevOrderId) => {
            this.prevOrderId$.next(prevOrderId);
          });
        this.orderService.getNextOrderId(orderId).subscribe((nextOrderId) => {
          this.nextOrderId$.next(nextOrderId);
        });
      }

      this.store.dispatch(
        orderActions.loadItem({
          id: orderId,
          include: {
            customerProfile: {
              include: {
                user: true,
              },
            },
            orderSource: true,
            orderStatus: true,
            orderServiceLinkages: {
              include: {
                offeredService: true,
              },
            },

            _count: {
              select: {
                orderCards: {
                  where: {
                    orderCardServiceLinkages: {
                      some: {
                        isDraft: true,
                      },
                    },
                  },
                },
                orderServiceLinkages: {
                  where: {
                    isDraft: true,
                  },
                },
                invoices: {
                  where: {
                    OR: [
                      {
                        status: {
                          not: INVOICE_STATUS.PAID,
                        },
                      },
                      {
                        refundItems: {
                          some: {
                            status: { not: REFUND_ITEM_STATUS.REFUNDED },
                          },
                        },
                      },
                    ],
                  },
                },
              },
            },
          },
        }),
      );
    });

    this.summaryTableConfig = {
      ...this.summaryTableConfig,
      data$: this.order$.pipe(
        map((order) => [
          {
            attribute: 'Customer Name',
            value: `${order?.customerProfile?.firstName ?? ''} ${
              order?.customerProfile?.lastName ?? ''
            }`,
          },
          {
            attribute: 'Customer Email',
            value: order?.customerProfile?.user?.email ?? '',
            href: `mailto:${order?.customerProfile?.user?.email}`,
          },
          {
            attribute: 'Customer Address',
            value: order?.customerProfile
              ? formatAddress({
                  line1: order!.customerProfile!.addressLine1,
                  line2: order!.customerProfile!.addressLine2,
                  city: order!.customerProfile!.city,
                  state: order!.customerProfile!.state,
                  zip: order!.customerProfile!.zipCode,
                  country: order!.customerProfile!.country,
                })
              : '',
          },
          {
            attribute: 'Additional Services',
            value: order?.orderServiceLinkages
              ?.map((li) => li.offeredService?.name ?? '')
              .join(', '),
          },
          {
            attribute: 'Order Submitted At',
            value: order?.submittedAt
              ? formatDateTime(order.submittedAt)
              : 'N/A',
          },
          {
            attribute: 'Order Received At',
            value: order?.receiptDate
              ? formatDateTime(order.receiptDate)
              : 'N/A',
          },
          {
            attribute: 'Order Notes',
            value: order?.notes,
          },
          {
            attribute: 'Customer Facing Notes',
            value: order?.customerFacingNotes,
          },
        ]),
      ),
    };

    // this.order$.subscribe((order) => {
    //   console.log('ORDER REFRESHED', order, JSON.parse(JSON.stringify(order)));
    // });
  }

  async openEditForm() {
    const order = await firstValueFrom(this.order$);
    if (!order) return;
    const data: OrderFormModalData = {
      mode: 'update',
      model: {
        ...order,
        requestedServices: {
          draftValues: order.orderServiceLinkages
            ?.filter((li) => li.isDraft)
            ?.map((li) => li.offeredServiceId),
          values: order.orderServiceLinkages
            ?.filter((li) => !li.isDraft)
            ?.map((li) => li.offeredServiceId),
        } as MultipleRequestedServicesFormFieldValue,
      },
    } as any;
    this.dialog.open(OrderFormModalComponent, {
      data,
      minWidth: '50vw',
      maxWidth: 'calc(100vw - 3rem)',
      maxHeight: 'calc(100vh - 3rem)',
      autoFocus: false,
    });
  }

  async promptDeleteItem() {
    const order = await firstValueFrom(this.order$);
    const data: ConfirmDialogData = {
      title: 'Are you sure you want to delete this item?',
      subtitle: 'This action cannot be undone.',
    };
    const confirmation = await lastValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          autoFocus: false,
          data,
        })
        .afterClosed(),
    );

    if (confirmation && order) {
      // TODO: make this also work with non 'id' primary keys
      this.store.dispatch(orderActions.deleteItem({ id: order.id }));
      this.router.navigate(['/']);
    }
  }

  async updateOrderStatus(newStatusId: number) {
    const orderId = await firstValueFrom(this.orderId$);
    if (orderId) {
      this.store.dispatch(
        orderActions.updateItem({
          id: orderId,
          data: {
            orderStatusId: newStatusId,
          },
        }),
      );
    }
  }

  async updateOrderSource(newSourceId: number) {
    const orderId = await firstValueFrom(this.orderId$);
    if (orderId) {
      this.store.dispatch(
        orderActions.updateItem({
          id: orderId,
          data: {
            orderSourceId: newSourceId,
          },
        }),
      );
    }
  }

  async toggleShowChangesToCustomer() {
    const orderId = await firstValueFrom(this.orderId$);
    const showChangesToCustomer = (await firstValueFrom(this.order$))
      ?.showChangesToCustomer;
    this.store.dispatch(
      orderActions.updateItem({
        id: orderId,
        data: {
          showChangesToCustomer: !showChangesToCustomer,
        },
      }),
    );
  }

  async sendChangeEmail() {
    const data: ConfirmDialogData = {
      title:
        'Are you sure you want to send these drafted changes to the customer?',
    };
    const confirmation = await lastValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          autoFocus: false,
          data,
        })
        .afterClosed(),
    );

    if (!confirmation) return;
    const id = await firstValueFrom(this.orderId$);
    this.store.dispatch(
      orderActions.sendChangeEmail({
        id,
      }),
    );
  }

  async clearDraftedChanges() {
    const data: ConfirmDialogData = {
      title: 'Are you sure you want to clear these drafted changes?',
      subtitle:
        'This will also cancel all auto drafted invoice items and pending refunds.',
    };
    const confirmation = await lastValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          autoFocus: false,
          data,
        })
        .afterClosed(),
    );

    if (!confirmation) return;
    const orderId = await firstValueFrom(this.orderId$);
    const cards = await firstValueFrom(
      this.store.select((s) => selectOrderCardState(s).items),
    );

    this.store.dispatch(invoiceActions.cancelAllForOrder({ id: orderId }));

    for (const card of cards) {
      const hasDraftedChanges = !!card.orderCardServiceLinkages?.find(
        (li) => li.isDraft,
      );

      if (!hasDraftedChanges) continue;

      for (const serviceLinkage of card.orderCardServiceLinkages ?? []) {
        if (serviceLinkage.isDraft) {
          this.store.dispatch(
            orderCardServiceLinkageActions.deleteItem({
              id: serviceLinkage.id,
            }),
          );
        }
      }
    }

    this.store.dispatch(
      orderActions.updateItem({
        id: orderId,
        data: {
          changesApproved: null as any,
        },
      }),
    );
  }

  async finalizeDraftedChanges() {
    const data: ConfirmDialogData = {
      title: 'Are you sure you want to finalize these drafted changes?',
      subtitle:
        'This will also finalize all auto drafted invoice items and pending refunds.',
    };
    const confirmation = await lastValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          autoFocus: false,
          data,
        })
        .afterClosed(),
    );

    if (!confirmation) return;
    const orderId = await firstValueFrom(this.orderId$);
    const order = await firstValueFrom(this.order$);
    const cards = await firstValueFrom(
      this.store.select((s) => selectOrderCardState(s).items),
    );

    const orderHasDraftedChanges = !!order!.orderServiceLinkages?.find(
      (li) => li.isDraft,
    );

    let deletedIds: number[] = [];
    if (orderHasDraftedChanges) {
      for (const serviceLinkage of order!.orderServiceLinkages ?? []) {
        if (serviceLinkage.isDraft) {
          // check if we already have service
          const existingLinkage = order!.orderServiceLinkages?.find(
            (existing) =>
              existing.offeredServiceId == serviceLinkage.offeredServiceId &&
              !existing.isDraft,
          );

          if (existingLinkage) {
            this.store.dispatch(
              orderServiceLinkageActions.updateItem({
                id: serviceLinkage.id,
                data: {
                  refundItemId: existingLinkage.refundItemId ?? undefined,
                  invoiceItemId: existingLinkage.invoiceItemId ?? undefined,
                  isDraft: false,
                },
              }),
            );

            if (!deletedIds.includes(existingLinkage.id)) {
              this.store.dispatch(
                orderServiceLinkageActions.deleteItem({
                  id: existingLinkage.id,
                }),
              );

              deletedIds = [...deletedIds, existingLinkage.id];
            }
          } else {
            this.store.dispatch(
              orderServiceLinkageActions.updateItem({
                id: serviceLinkage.id,
                data: {
                  isDraft: false,
                },
              }),
            );
          }
        } else {
          if (
            !deletedIds.includes(serviceLinkage.id) &&
            !order!.orderServiceLinkages?.find(
              (existing) =>
                existing.offeredServiceId == serviceLinkage.id &&
                existing.isDraft,
            )
          ) {
            this.store.dispatch(
              orderCardServiceLinkageActions.deleteItem({
                id: serviceLinkage.id,
              }),
            );
            deletedIds = [...deletedIds, serviceLinkage.id];
          }
        }
      }
    }

    deletedIds = [];
    for (const card of cards) {
      const hasDraftedChanges = !!card.orderCardServiceLinkages?.find(
        (li) => li.isDraft,
      );

      if (!hasDraftedChanges) continue;

      for (const serviceLinkage of card.orderCardServiceLinkages ?? []) {
        if (serviceLinkage.isDraft) {
          // check if we already have service
          const existingLinkage = card.orderCardServiceLinkages?.find(
            (existing) =>
              existing.offeredServiceId == serviceLinkage.offeredServiceId &&
              !existing.isDraft,
          );

          if (existingLinkage) {
            this.store.dispatch(
              orderCardServiceLinkageActions.updateItem({
                id: serviceLinkage.id,
                data: {
                  refundItemId: existingLinkage.refundItemId ?? undefined,
                  invoiceItemId: existingLinkage.invoiceItemId ?? undefined,
                  isDraft: false,
                },
              }),
            );

            if (!deletedIds.includes(existingLinkage.id)) {
              this.store.dispatch(
                orderCardServiceLinkageActions.deleteItem({
                  id: existingLinkage.id,
                }),
              );

              deletedIds = [...deletedIds, existingLinkage.id];
            }
          } else {
            this.store.dispatch(
              orderCardServiceLinkageActions.updateItem({
                id: serviceLinkage.id,
                data: {
                  isDraft: false,
                },
              }),
            );
          }
        } else {
          if (
            !deletedIds.includes(serviceLinkage.id) &&
            !card.orderCardServiceLinkages?.find(
              (existing) =>
                existing.offeredServiceId == serviceLinkage.id &&
                existing.isDraft,
            )
          ) {
            this.store.dispatch(
              orderCardServiceLinkageActions.deleteItem({
                id: serviceLinkage.id,
              }),
            );
            deletedIds = [...deletedIds, serviceLinkage.id];
          }
        }
      }
    }

    this.store.dispatch(invoiceActions.finalizeAllForOrder({ id: orderId }));

    setTimeout(() => {
      this.store.dispatch(
        orderActions.updateItem({
          id: orderId,
          data: {
            hasPendingServiceChanges: false,
            changesApproved: null as any,
          },
        }),
      );
    }, 3000);
  }

  selectedIndexChanged(index: number) {
    this.currentTabIndex$.next(index);
  }

  async navigateToPrevOrder() {
    const prevOrderId = await firstValueFrom(this.prevOrderId$);
    if (prevOrderId) {
      this.store.dispatch(orderCardActions.clearCurrent());
      this.router.navigate(['orders', prevOrderId]);
    }
  }

  async navigateToNextOrder() {
    const nextOrderId = await firstValueFrom(this.nextOrderId$);
    if (nextOrderId) {
      this.store.dispatch(orderCardActions.clearCurrent());
      this.router.navigate(['orders', nextOrderId]);
    }
  }

  notifyCustomerOfCardChanges(orderCardId: number) {
    this.store.dispatch(
      orderCardActions.notifyCustomerOfCardChanges({ orderCardId }),
    );
  }

  async clearDraftChangesForCard(orderCardId: number) {
    const data: ConfirmDialogData = {
      title: 'Are you sure you want to clear draft changes for this card?',
      subtitle: 'This will remove all draft service linkages for this card.',
    };
    const confirmation = await lastValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, { autoFocus: false, data })
        .afterClosed(),
    );
    if (!confirmation) return;

    const cards = await firstValueFrom(this.cards$);
    const card = cards.find((c) => c.id === orderCardId);
    if (!card) return;

    for (const linkage of card.orderCardServiceLinkages ?? []) {
      if ((linkage as any).isDraft) {
        this.store.dispatch(
          orderCardServiceLinkageActions.deleteItem({ id: linkage.id }),
        );
      }
    }
  }

  async cancelAndClearDraftChangesForCard(orderCardId: number) {
    const data: ConfirmDialogData = {
      title: 'Are you sure you want to cancel and clear draft changes for this card?',
      subtitle:
        'This will cancel the pending approval request and remove all draft service linkages and associated invoicing.',
    };
    const confirmation = await lastValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, { autoFocus: false, data })
        .afterClosed(),
    );
    if (!confirmation) return;

    const cards = await firstValueFrom(this.cards$);
    const card = cards.find((c) => c.id === orderCardId);
    if (!card) return;

    this.store.dispatch(
      orderCardActions.rejectCardChanges({
        orderCardId,
        messageId: (card as any).threads?.[0]?.messages?.[0]?.id,
        metadata: {
          ...(card as any).threads?.[0]?.messages?.[0]?.metadata,
          status: 'rejected',
        },
      }),
    );

    for (const linkage of card.orderCardServiceLinkages ?? []) {
      if ((linkage as any).isDraft) {
        this.store.dispatch(
          orderCardServiceLinkageActions.deleteItem({ id: linkage.id }),
        );
      }
    }
  }

  async viewCardInChat(orderCardId: number) {
    const orderId = await firstValueFrom(this.orderId$);
    this.currentTabIndex$.next(0);
    this.router.navigate(['orders', orderId], {
      queryParams: { threadOrderCardId: orderCardId },
    });
  }

  async canDeactivate(): Promise<boolean> {
    const order = await firstValueFrom(this.order$);
    if (!isCardLevelChangeFlow(order?.createdAt)) return true;

    const unsentChanges = await firstValueFrom(this.cardsWithUnsentChanges$);
    if (!unsentChanges.length) return true;

    const data: ConfirmDialogData = {
      title: 'You have draft changes that have not been sent to the customer.',
      subtitle: 'Are you sure you want to leave this page?',
    };
    const confirmation = await lastValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          autoFocus: false,
          data,
        })
        .afterClosed(),
    );

    return !!confirmation;
  }

  getTextColorForBackground = computeTextColorForBackground;

  formatDateTime = formatDateTime;
}
